import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Typography, Button, Collapse, IconButton, Alert, TextField } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useUser } from "../../context/userContext";
import { getAgentsByGroupId, getAllAgentGroups, getAllAgents } from "../../api/userApi";
import { getPricing, getPricingHistories } from "../../api/resourceApi";
import { createSales, checkDuplicateSale } from "../../api/salesApi";
import { convertDateStringToDateTime } from '../../utils/datetimeUtils';
import { useNavigate, useLocation } from "react-router-dom";
import GenericSalesForm from "../common/genericSalesForm";
import SaleFormDetails from "./saleFormDetails";
import { buildFrozenPricing } from "../../utils/frozenPricing";
import { backupboxRemoval, suriaHomeBonus } from "../../utils/rscPromos";
import { methodBreakdown, termForMethod, PAYMENT_METHOD_OPTIONS, isMixMethod, mixBreakdown } from "../../utils/ccPricing";
import { sstOn } from "../../utils/sst";
import { UseFetchAgentGroupData } from "../../hooks/useFetchAgent";
import QuickAddReferralDialog from "./quickAddReferralDialog";
import { attachSaleToLead } from "../../api/referralLinkApi";

const PP = "'Poppins', sans-serif";

function todayDDMMYYYY() {
    const d = new Date();
    const p = n => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

// Full PDF "Form Details" snapshot template so the section renders the SAME
// fields as an RSC/CSC-created sale (SaleFormDetails only shows keys that exist
// in the payload). Seeded empty; the user fills them on the create form.
function defaultFormPayload(category) {
    const common = {
        salutation: 'MR.',
        name: '', icNumber: '', phone: '', email: '',
        tinNumber: '', address: '', tnbAccountNo: '',
        date: todayDDMMYYYY(),
        emergencyName: '', emergencyRelationship: '', emergencyIc: '',
        emergencyPhone: '', emergencyAddress: '', emergencyEmail: '',
        operationWarrantyYears: '1 year',
        insuranceAmount: 10000,
        depositPaymentMethod: '',
        paymentMethodLabel: '',
        quotationPaymentPlan: 36,
    };
    if (String(category).toUpperCase() === 'COMMERCIAL') {
        return {
            ...common,
            companyName: '', companyTin: '', companyOffice: '', companyFax: '', companyEmail: '', companyWebsite: '',
            picSalutation: 'MR.', picName: '', picMykad: '', picPosition: '', picCitizenship: '', picMobile: '', picEmail: '',
            altSalutation: 'MR.', altName: '', altMykad: '', altPosition: '', altRelationship: '', altMobile: '', altEmail: '',
        };
    }
    return common;
}

export default function CreateSalesForm() {
    const navigate = useNavigate();

    const { user } = useUser();
    const userId = user?.userId;

    const [successMsg, setSuccessMsg] = useState("");
    // const [loading, setLoading] = useState(true);
    // const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // ===== Agent Details States =====
    const { agentGroup, agentNameOptions, loading: agentLoading } = UseFetchAgentGroupData(userId);
    const [selectedAgentId, setSelectedAgentId] = useState(userId);
    const [coBrokeGroupOptions, setCoBrokeGroupOptions] = useState([]);
    const [coBrokeNameOptions, setCoBrokeNameOptions] = useState([]);
    const [selectedCoBrokeGroupId, setSelectedCoBrokeGroupId] = useState();
    const [selectedCoBrokeAgentId, setSelectedCoBrokeAgentId] = useState();
    const [referralOptions, setReferralOptions] = useState([]);
    const [selectedReferralId, setSelectedReferralId] = useState('');
    // Astern bears it unless an admin says otherwise.
    const [referralPaidByAstern, setReferralPaidByAstern] = useState(true);
    // Who may move the cost. An agent must not be able to decide that their own
    // referral comes out of the company rather than out of them, so the control is
    // read-only for everyone else. (user 2026-10-13: "they cant adjust, only we can")
    const isAdminLike = (user?.roles || []).some(r =>
        ['Super Admin', 'Admin', 'Astern Admin', 'Accountant'].includes(r));

    // ===== Customer Details States =====
    const [customerName, setCustomerName] = useState("");
    const [customerState, setCustomerState] = useState("");
    const [customerCity, setCustomerCity] = useState("");
    const [customerPostcode, setCustomerPostcode] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [customerAddress, setCustomerAddress] = useState("");
    // Site Visit answers — facts about the address, captured once at the sale.
    const [siteGatedGuarded, setSiteGatedGuarded] = useState(false);
    const [mgmtApprovalNeeded, setMgmtApprovalNeeded] = useState(false);
    const [siteVisitRequired, setSiteVisitRequired] = useState(true);

    // ===== Category and Product States =====
    const [selectedCategory, setSelectedCategory] = useState("RESIDENTIAL");
    const [productList, setProductList] = useState([]);

    // ===== Panel and System Size States =====
    const [panelQty, setPanelQty] = useState(0);
    const [energy, setEnergy] = useState(0);
    // RSC hand-off seeds the panel-wattage dropdown (UI-only). Null → form default 640W.
    const [rscPanelWatt, setRscPanelWatt] = useState(null);

    // ===== Payment Method States =====
    // Shared canonical list — IDENTICAL to the RSC calculator dropdown.
    const availablePaymentMethods = PAYMENT_METHOD_OPTIONS;
    const [paymentMethod, setPaymentMethod] = useState("CASH");
    // The card's issuing bank. Only bites at 60 months, where the rate is the bank's
    // (7.20%–14.00%) rather than one flat number — and since the commission basis is
    // the converted price, the bank decides what the agent earns too.
    // (user 2026-10-29: "the % of 60m will follow the rate of the bank aso")
    const [ccBank, setCcBank] = useState('');
    const [ccBankMethod, setCcBankMethod] = useState('');
    // Wiring cable brand for the quotation (Any / KUKA Cable / HELUKABEL). (user 2026-07-24)
    const [cableBrand, setCableBrand] = useState('Any');
    // MIX (cash + CC): cash paid upfront + the term the remainder finances on. (user 2026-07-22)
    const [mixCashAmount, setMixCashAmount] = useState('');
    const [mixTerm, setMixTerm] = useState(36);

    // ===== Price States =====
    const [originalPrice, setOriginalPrice] = useState(0);
    const [canEditPrice, setCanEditPrice] = useState(false);

    const [addOnPrice, setAddOnPrice] = useState(0);

    // ===== Residential Matching State =====
    const [residentialMatch, setResidentialMatch] = useState(null);

    // ===== Pricing snapshot (history) =====
    // null = LIVE/current price list. When the user picks a historical snapshot, the
    // sale is priced from THAT snapshot (price + panel-kWp mapping) and the chosen
    // historyId is persisted so the edit page re-loads the same list.
    const [pricingSnapshotId, setPricingSnapshotId] = useState(null);
    const [pricingSnapshots, setPricingSnapshots] = useState([]);
    useEffect(() => {
        getPricingHistories().then(r => setPricingSnapshots(r.data || [])).catch(() => {});
    }, []);
    // Panel wattage of the ACTIVE ATAP list (kWp ÷ panels of any row) — uniform across a
    // list (current will be 650W, the old snapshot is 620W). The wattage field follows
    // THIS by default for both current and old pricing, so the label + kWp + price always
    // agree with the selected list. null only until the list has loaded.
    const atapPanelWatt = useMemo(() => {
        const row = (productList || []).find(r => Number(r.panelQty) > 0 && Number(r.energy) > 0);
        return row ? Math.round(Number(row.energy) * 1000 / Number(row.panelQty)) : null;
    }, [productList]);

    // RSC-style sizing verdict (inverter / ratio / SEDA-CCC) from the form — gates submit.
    const [sizing, setSizing] = useState(null);

    // ===== Phase Mode (Residential only) =====
    // Drives which ATAP pricing column getPackagePrice picks: cashPrice/cC36Price/cC60Price (single)
    // vs cash3p8k/cC36_3p8k/cC60_3p8k (3-phase). Default single — 3-phase is the explicit override.
    const [phaseMode, setPhaseMode] = useState('single');

    // ===== Pricing Calculation States =====
    const [finalPrice, setFinalPrice] = useState();
    const [asternDiscount, setAsternDiscount] = useState();
    const [agentDiscount, setAgentDiscount] = useState();
    // "Deduct SST" (commercial): the typed discount deducts the SST first. (user 2026-08-10)
    const [deductSst, setDeductSst] = useState(false);
    const [depositOn, setDepositOn] = useState();

    // ===== Upline / Team Discount (2026-08-19) =====
    // Two NEW discount buckets alongside Astern + Agent. They are real price cuts —
    // FinalPrice = Original − Astern − Agent − Upline − Team — and the backend charges
    // them to the seller's uplines (DOC) and leaders (TOC) via SplitPercent.
    // Deliberately PLAIN amount inputs: no warning, no guard, no per-person figure. A
    // live per-person number would expose commission to whoever keys the sale
    // (spec decision ③ / §12, user 2026-08-19). Negatives only surface on the slip.
    // These SUPERSEDE the retired "Discount Funded By" control (spec §12a): "agent bears
    // 200, upline bears 400" is now Agent Discount 200 + Upline Discount 400.
    const [uplineDiscount, setUplineDiscount] = useState('');
    const [teamDiscount, setTeamDiscount] = useState('');

    // ===== Add-on Line Items =====
    const [lineItems, setLineItems] = useState([]);

    // ===== RSC-style promos (auto-fill Astern discount) — default BLANK =====
    // Promos must NOT be pre-selected: the auto-fill only fires when the agent
    // deliberately ticks one, so the Astern discount is never silently set behind
    // their back. See feedback_price_freeze_no_retroactive_atap.
    // Default-ticked on create (auto-fills the Astern discount); Per-Battery stays off
    // (only applies once a 16 kWh battery is added).
    const [promoBackupbox, setPromoBackupbox] = useState(true);
    const [promoFirst640, setPromoFirst640] = useState(true);
    const [promoPerBatt, setPromoPerBatt] = useState(false);

    // Astern discount follows the SAME RSC promo rules (shared utils/rscPromos): SuRIA +
    // backupbox-removal, driven by the promo toggles / phase / battery — so create-sales
    // stays in lock-step with RSC (SuRIA phase-dependent: kept 1,800/3,000 · removed 800/1,600;
    // backupbox removal 1,600/2,500) — reverted to the pre-08-06 set on 2026-08-13.
    // A deliberately-typed figure wins (asternTouched). Create-only; edits use editSalePage.
    // (user 2026-08-10)
    const asternTouched = useRef(false);
    const _hasBattery = (lineItems || []).some(li => li.isBattery);
    const _boxRemoved = promoBackupbox && !_hasBattery;
    const _is3p = phaseMode === '3phase';
    const _promoAstern = (_boxRemoved ? backupboxRemoval(_is3p) : 0) + (promoFirst640 ? suriaHomeBonus(_boxRemoved, _is3p) : 0);
    useEffect(() => {
        if (asternTouched.current) return;
        setAsternDiscount(_promoAstern);
    }, [_promoAstern]);
    const handleAsternChange = (v) => { asternTouched.current = true; setAsternDiscount(v); };

    // ===== Form Details (for PDFs) — same section as the sale page =====
    const [formDetailsOpen, setFormDetailsOpen] = useState(false);
    const [formPayload, setFormPayload] = useState(() => defaultFormPayload('RESIDENTIAL'));

    // Re-seed the snapshot keys when the category changes (residential <-> commercial),
    // preserving any values the user already typed for keys that still apply.
    useEffect(() => {
        setFormPayload(prev => {
            const base = defaultFormPayload(selectedCategory);
            const merged = { ...base };
            if (prev) for (const k of Object.keys(base)) if (prev[k] !== undefined && prev[k] !== '') merged[k] = prev[k];
            return merged;
        });
    }, [selectedCategory]);

    // RSC hand-off: when the user clicks "Create Sale" in the RSC calculator, it
    // navigates here with state.rscPrefill — we map its data into this form so EVERY
    // sale goes through this one consistent page (RSC mechanism unchanged). One-shot.
    const location = useLocation();
    const rscPrefillRef = useRef(false);
    // RSC folds the inverter-upgrade cost into its system price, so the prefilled
    // originalPrice can differ from the live package list. Keep it pinned (against the
    // auto-price effect below) until the agent changes the kWp away from the prefilled
    // value — at which point normal auto-fill resumes.
    const prefillPriceRef = useRef(null);
    const prefillEnergyRef = useRef(null);
    useEffect(() => {
        if (rscPrefillRef.current) return;
        const pre = location.state?.rscPrefill;
        if (!pre) return;
        rscPrefillRef.current = true;
        if (pre.selectedCategory) setSelectedCategory(pre.selectedCategory);
        if (pre.phaseMode) setPhaseMode(pre.phaseMode);
        if (pre.panelQty != null) setPanelQty(pre.panelQty);
        if (pre.energy != null) setEnergy(pre.energy);
        if (pre.panelWatt != null) setRscPanelWatt(pre.panelWatt);
        if (pre.paymentMethod) setPaymentMethod(pre.paymentMethod);
        if (pre.ccBank) setCcBank(pre.ccBank);
        if (pre.ccBankMethod) setCcBankMethod(pre.ccBankMethod);
        if (pre.cableBrand) setCableBrand(pre.cableBrand);   // RSC → create-sales cable brand
        else if (pre.formPayload?.cableBrand) setCableBrand(pre.formPayload.cableBrand);
        if (pre.originalPrice != null) {
            setOriginalPrice(pre.originalPrice); setCanEditPrice(true);
            prefillPriceRef.current = pre.originalPrice;
            prefillEnergyRef.current = Number(pre.energy);
        }
        if (pre.asternDiscount != null) setAsternDiscount(pre.asternDiscount);
        if (pre.agentDiscount != null) setAgentDiscount(pre.agentDiscount);
        if (pre.addOnPrice != null) setAddOnPrice(pre.addOnPrice);
        if (Array.isArray(pre.lineItems)) setLineItems(pre.lineItems);
        if (pre.promoBackupbox != null) setPromoBackupbox(!!pre.promoBackupbox);
        if (pre.promoFirst640 != null) setPromoFirst640(!!pre.promoFirst640);
        if (pre.promoPerBatt != null) setPromoPerBatt(!!pre.promoPerBatt);
        if (pre.customerName) setCustomerName(pre.customerName);
        if (pre.customerPhone) setCustomerPhone(pre.customerPhone);
        if (pre.customerEmail) setCustomerEmail(pre.customerEmail);
        if (pre.customerAddress) setCustomerAddress(pre.customerAddress);
        if (pre.customerState) setCustomerState(pre.customerState);
        if (pre.customerCity) setCustomerCity(pre.customerCity);
        if (pre.customerPostcode) setCustomerPostcode(pre.customerPostcode);
        // Asked once, in the RSC, standing at the property. `!= null` rather than a plain
        // truthiness check so an explicit `false` still arrives as an answer.
        if (pre.siteGatedGuarded != null) setSiteGatedGuarded(!!pre.siteGatedGuarded);
        if (pre.mgmtApprovalNeeded != null) setMgmtApprovalNeeded(!!pre.mgmtApprovalNeeded);
        // Arriving from a referral lead: the introduction already names the referrer, so
        // the Referral box is filled in rather than left for somebody to remember.
        if (pre.selectedReferralId) setSelectedReferralId(pre.selectedReferralId);
        if (pre.selectedAgentId) setSelectedAgentId(pre.selectedAgentId);
        if (pre.selectedCoBrokeGroupId) setSelectedCoBrokeGroupId(pre.selectedCoBrokeGroupId);
        if (pre.selectedCoBrokeAgentId) setSelectedCoBrokeAgentId(pre.selectedCoBrokeAgentId);
        if (pre.formPayload) setFormPayload(prev => ({ ...(prev || {}), ...pre.formPayload }));
    }, [location.state]);

    const handleFormFieldChange = (key, value) => {
        setFormPayload(prev => ({ ...(prev || {}), [key]: value }));
    };

    // ===== Possible-duplicate warning (WARN-only, never blocks submit) =====
    const [dupMatches, setDupMatches] = useState([]);
    const dupTimerRef = useRef(null);
    const customerIc = formPayload?.icNumber || '';
    useEffect(() => {
        if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
        const name = (customerName || '').trim();
        const phone = (customerPhone || '').trim();
        const ic = (customerIc || '').trim();
        if (!name && !phone && !ic) { setDupMatches([]); return; }
        dupTimerRef.current = setTimeout(() => {
            checkDuplicateSale({ name, phone, ic })
                .then(r => setDupMatches(Array.isArray(r.data) ? r.data : []))
                .catch(() => setDupMatches([]));
        }, 500);
        return () => { if (dupTimerRef.current) clearTimeout(dupTimerRef.current); };
    }, [customerName, customerPhone, customerIc]);

    // Combined one-line address (same format the RSC builder + sale page use) so
    // the PDF snapshot address matches the split Customer-section fields.
    const combinedAddress = [
        customerAddress,
        [customerPostcode, customerCity].filter(s => s && String(s).trim()).join(' '),
        customerState,
    ].filter(s => s && String(s).trim()).join(', ').toUpperCase();

    useEffect(() => {
        if (userId) {
            setSelectedAgentId(userId);
        }
    }, [userId]);

    useEffect(() => {
        const fetchAgentGroups = async () => {
            try {
                const response = await getAllAgentGroups();
                if (response.status === 200) {
                    setCoBrokeGroupOptions(response.data);
                }
                else {
                    console.error("Unexpected response:", response);
                }
            }
            catch (error) {
                console.error("Error fetching Agent groups:", error);
            }
        }
        fetchAgentGroups();
    }, []);

    /* A referrer made from inside the sale: the new one is added to the picker and
       selected, so the agent carries on where they were rather than reloading the form
       and finding their way back. (user 2026-10-19) */
    const [quickReferralOpen, setQuickReferralOpen] = useState(false);
    const handleReferralCreated = (created) => {
        if (!created?.userId) return;
        setReferralOptions(prev => [...prev, {
            userId: created.userId,
            firstName: created.firstName,
            lastName: created.lastName,
            username: created.username,
            status: 'Active',
            userRoles: [{ roleName: 'Referral' }],
        }]);
        setSelectedReferralId(created.userId);
        setQuickReferralOpen(false);
    };

    // Load Referral-role users for the optional Referral picker.
    useEffect(() => {
        getAllAgents().then(r => {
            const refs = (r.data || []).filter(u =>
                u.status === 'Active' && (u.userRoles || []).some(ur => ur.roleName === 'Referral')
            );
            setReferralOptions(refs);
        }).catch(err => console.error("Error fetching referral users:", err));
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                // getPricing(snapshotId): null → LIVE list; a historyId → that snapshot,
                // returned in the SAME ResourcePricing shape so productList/residentialMatch
                // (and the frozen price on create) derive identically from either source.
                const response = await getPricing(pricingSnapshotId);
                if (response.status === 200) {
                    // Sales form only needs the Residential Pricing list (ATAP Pricing page source)
                    setProductList((response.data || []).filter(r => r.listName === 'Residential Pricing'));
                }
                else {
                    console.error("Unexpected response:", response);
                }
            }
            catch (error) {
                console.error("Error fetching pricing:", error);
            }
        }
        fetchProducts();
    }, [pricingSnapshotId]);

    // Map sales form paymentMethod values to ResourcePricing field names.
    // Honors phaseMode: 'single' uses cashPrice/cC36Price/cC60Price, '3phase' uses cash3p8k/etc.
    // Falls back to the other phase if the chosen column is null/0 (rare edge case).
    // Original Price is the CASH base (Section 1) — always the cash column,
    // regardless of payment method. The Payment Method section converts the whole
    // thing to the chosen basis (cash ÷ factor) for saving / quotation / commission.
    const getPackagePrice = (match, _method, phase = 'single') => {
        return phase === '3phase'
            ? (Number(match.cash3p8k) || Number(match.cashPrice) || 0)
            : (Number(match.cashPrice) || Number(match.cash3p8k) || 0);
    };

    // // ===== Calculate Energy (kWp) =====
    // useEffect(() => {
    //     if (panelQty > 0) {
    //         const calculatedSize = Number((panelQty * 0.62).toFixed(2));
    //         setEnergy(calculatedSize);
    //     }
    //     else {
    //         setEnergy(0);
    //     }
    // }, [panelQty]);

    // ===== Set Price Editing Permission Based on Category =====
    useEffect(() => {
        // Both Residential and Commercial can edit price
        setCanEditPrice(true);

        // Reset price when switching categories
        setOriginalPrice(0);
        setResidentialMatch(null);
    }, [selectedCategory]);

    // ===== RESIDENTIAL LOGIC: Auto-populate Price =====
    // Match by kWp only (admin sets pricing by kWp, not by panel count × wattage).
    // If no match, agent can still enter price manually — submission is not blocked.
    useEffect(() => {
        if (selectedCategory === "RESIDENTIAL" && energy > 0) {
            const match = productList.find(p => Number(p.energy) === Number(energy));
            setResidentialMatch(match);

            // Re-assert an RSC-prefilled price while the kWp is unchanged (it folds in
            // the inverter upgrade, which the live package list doesn't). We actively
            // SET it — not just "keep current" — because the category-reset effect and
            // mount ordering can blank originalPrice after the prefill ran. Once the
            // agent changes the kWp, drop the pin and resume normal auto-fill.
            if (prefillPriceRef.current != null && Number(energy) === prefillEnergyRef.current) {
                setOriginalPrice(prefillPriceRef.current);
                return;
            }
            prefillPriceRef.current = null;

            if (match && paymentMethod) {
                setOriginalPrice(getPackagePrice(match, paymentMethod, phaseMode));
            }
            else if (!match) {
                setOriginalPrice(0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, panelQty, energy, paymentMethod, productList, phaseMode]);

    // ===== Calculate Net Price =====
    // FinalPrice = Original − Astern − Agent − Upline − Team (spec §2).
    useEffect(() => {
        const oriPrice = originalPrice || 0;
        const asternDisc = asternDiscount || 0;
        const agentDisc = agentDiscount || 0;
        const uplineDisc = Number(uplineDiscount) || 0;
        const teamDisc = Number(teamDiscount) || 0;
        const afterDisc = oriPrice - asternDisc - agentDisc - uplineDisc - teamDisc;

        setFinalPrice(Math.max(0, afterDisc));

    }, [originalPrice, asternDiscount, agentDiscount, uplineDiscount, teamDiscount]);

    // Manual group change: switch group + clear the agent. The co-broke agent
    // OPTIONS are loaded by the effect below (keyed on selectedCoBrokeGroupId),
    // so the RSC prefill path — which sets the group id directly without firing
    // this onChange — also gets its agent list populated and can render the
    // prefilled Co-Agent instead of appearing empty.
    const handleAgentGroupChange = (event) => {
        setSelectedCoBrokeGroupId(event.target.value);
        setSelectedCoBrokeAgentId("");
    };

    // Load co-broke agent options whenever the co-broke group changes (manual OR
    // via prefill). Never touches selectedCoBrokeAgentId, so a prefilled agent
    // survives until its options arrive and it can render.
    useEffect(() => {
        let cancelled = false;
        if (!selectedCoBrokeGroupId) { setCoBrokeNameOptions([]); return; }
        getAgentsByGroupId(selectedCoBrokeGroupId)
            .then(res => {
                if (cancelled) return;
                const filtered = (res.data || []).filter(a => String(a.userId) !== String(user.userId));
                setCoBrokeNameOptions(filtered);
            })
            .catch(() => { if (!cancelled) setCoBrokeNameOptions([]); });
        return () => { cancelled = true; };
    }, [selectedCoBrokeGroupId, user.userId]);

    const handleCancel = (event) => {
        event.preventDefault();
        navigate("/sales");
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (submitting) {
            return;
        }

        if (!customerName || !customerPhone || !customerAddress || !customerState || !customerCity || !paymentMethod) {
            alert("Please fill in all required fields.");
            return;
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (customerEmail) {
            if (!emailRegex.test(customerEmail)) {
                alert("Please enter a valid email address.");
                return;
            }
        }

        // ===== Validate panel quantity =====
        if (panelQty <= 0) {
            alert("Please enter a valid number of panels.");
            return;
        }

        // ===== RSC-style sizing gate — only a valid config can be created =====
        // (over-SEDA needs a CCC add-on; the safety ratio must be within cap).
        if (sizing && !sizing.valid) {
            alert(sizing.reason || "This configuration is not valid. Fix the inverter/SEDA/CCC issue before creating the sale.");
            return;
        }

        // No residential match: allow manual price entry (originalPrice validation below catches RM 0).

        const phoneDigits = customerPhone.replace(/\D/g, '');
        if (phoneDigits.length < 10 || phoneDigits.length > 12) {
            alert("Phone number must be between 10 and 12 digits.");
            return;
        }

        if (!originalPrice || originalPrice <= 0) {
            alert("Original price must be greater than 0.");
            return;
        }

        // Convert the CASH base (Section 1) to the chosen payment-method basis
        // (Section 2) — this is what's SAVED and what commission + quotation run on.
        // Cash = unchanged; CC = cash ÷ factor (prices round-up RM10, discounts
        // rounded). New sales only — existing sales are never recomputed.
        // Shared converter (RSC parity): system uses the CC price list; the TOTAL rounds
        // up the whole cash final ONCE; add-on = residual. Keeps save = display = quotation.
        // MIX (cash + CC): split the whole cash total by the cash amount; the rest ÷factor.
        // Commission = Original − Astern in MIX basis. Pure CC/cash use methodBreakdown.
        // (user 2026-07-22)
        // Upline / Team ride the SAME converter as the Agent discount — on a CC/MIX basis
        // they are divided by the CC factor (36M = 0.925: RM600 → 649, RM400 → 432).
        // Skipping that would silently under-apply the discount on every CC/MIX sale.
        // (spec Example F)
        const _upline = Number(uplineDiscount) || 0;
        const _team = Number(teamDiscount) || 0;
        const _isMix = isMixMethod(paymentMethod);
        const _bd = _isMix
            ? mixBreakdown({ originalPrice, asternDiscount, agentDiscount, uplineDiscount: _upline, teamDiscount: _team, addOnPrice }, Number(mixTerm) || 36, Number(mixCashAmount) || 0)
            : methodBreakdown({ originalPrice, asternDiscount, agentDiscount, uplineDiscount: _upline, teamDiscount: _team, addOnPrice },
                termForMethod(paymentMethod), { bank: ccBank, bankMethod: ccBankMethod || null });
        const sOriginal = _bd.original;
        const sAstern = _bd.astern;
        const sAgent = _bd.agent;
        const sUpline = _bd.upline;
        const sTeam = _bd.team;
        const sAddon = _bd.addon;
        const sFinal = _bd.final;
        // Customer-facing quotation total (payment denominator). Commercial + "Deduct SST":
        // the typed discount deducts the SST first — full SST = sstOn(original+add-on), SST
        // payable = max(0, fullSST − discount total), any excess comes off the price. So the
        // customer pays (original − excess) + add-on + SST payable. Otherwise the plain basis
        // total. (user 2026-08-10)
        // The discount total MUST include Upline + Team (spec Example E): omitting them
        // quotes the customer the full new discount too much (36,472.92 vs a wrong
        // 37,472.92 on sale 1931) while every other screen still looks right.
        const _sstQuote = (() => {
            if (selectedCategory !== 'COMMERCIAL' || !deductSst) return Number(_bd.total) || 0;
            const _disc = (Number(_bd.astern) || 0) + (Number(_bd.agent) || 0)
                + (Number(_bd.upline) || 0) + (Number(_bd.team) || 0);
            const _full = sstOn((Number(_bd.original) || 0) + (Number(_bd.addon) || 0));
            const _pay = Math.max(0, _full - _disc);
            const _excess = Math.max(0, _disc - _full);
            return (Number(_bd.original) || 0) - _excess + (Number(_bd.addon) || 0) + _pay;
        })();

        const newSalesInfo = {
            "agentId": selectedAgentId,
            "coAgentId": selectedCoBrokeAgentId || null,
            "referralUserId": selectedReferralId || null,
            "discountByAstern": sAstern || null,
            "discountByAgent": sAgent || null,
            // NEW buckets — funded by the seller's uplines (DOC) / leaders (TOC).
            "discountByUpline": sUpline || null,
            "discountByTeam": sTeam || null,
            "panelQty": panelQty,
            "energy": energy,
            "originalPrice": sOriginal,
            "finalPrice": sFinal,
            "addOnPrice": sAddon || null,
            // Customer-facing total (Cash/CC/MIX basis) — denominator for payment-% / claim state.
            "quotationTotal": _sstQuote || null,
            "lineItems": lineItems.length > 0 ? lineItems : null,
            "productCategory": selectedCategory,
            "paymentMethod": paymentMethod,
            "phase": phaseMode === '3phase' ? '3phase' : 'single',
            "depositOn": convertDateStringToDateTime(depositOn) || null,
            // PDF/form snapshot so generated forms match (mirrors the sale page).
            // Customer name/phone/email/address are the single source of truth.
            // Pricing is FROZEN here from the matched ATAP row as of now — later ATAP
            // price changes never alter this sale's quotation (only an edit does).
            "formPayloadJson": JSON.stringify({
                ...(formPayload || {}),
                ...buildFrozenPricing({
                    match: residentialMatch,
                    is3p: phaseMode === '3phase',
                    energy, panelQty, originalPrice,
                    asternDiscount, agentDiscount,
                    uplineDiscount: Number(uplineDiscount) || 0, teamDiscount: Number(teamDiscount) || 0,
                    addOnPrice, lineItems,
                    promoBackupbox, promoFirst640, promoPerBatt,
                    paymentMethod,
                    ccBank, ccBankMethod,
                    // MIX cash/term drive the "TOTAL PAYABLE (MIX)" row on the quotation.
                    mixCashAmount: Number(mixCashAmount) || 0, mixTerm: Number(mixTerm) || 36,
                }),
                // The bank is kept on the sale itself (no new column — the payload is
                // where the edit page and the signing link already read this sale's
                // pricing from), so reopening it re-derives on the SAME rate it was
                // created on rather than the flat 10%.
                ...(ccBank ? { ccBank, ccBankMethod: ccBankMethod || '' } : {}),
                // Pricing snapshot used to create this sale (null/absent = live list).
                // Persisted so the edit page re-loads the SAME historical price list.
                ...(pricingSnapshotId ? { pricingHistoryId: pricingSnapshotId } : {}),
                // MIX persistence — restored on the edit page. (user 2026-07-22)
                ...(_isMix ? { mixCashAmount: Number(mixCashAmount) || 0, mixTerm: Number(mixTerm) || 36, mixCashOriginal: Number(originalPrice) || 0 } : {}),
                // "Deduct SST" flag — the typed discount deducts SST (commercial). Persisted so
                // the edit page + quotation reconstruct the SST-deducted total. (user 2026-08-10)
                ...(deductSst ? { deductSst: true } : {}),
                // Wiring cable brand → quotation "Balance of System" line. (user 2026-07-24)
                cableBrand: cableBrand && cableBrand !== 'Any' ? cableBrand : '',
                name: customerName.toUpperCase(),
                phone: customerPhone || '',
                email: (customerEmail || '').toLowerCase(),
                address: combinedAddress,
            }),
            "createdBy": user.username,
            // RETIRED 2026-08-19 (spec §12a): discountFundedBy / agentDiscountContribution /
            // uplineDiscountContribution are no longer sent. The Upline/Team buckets above
            // subsume them — they price the customer correctly, split across EVERY upline by
            // SplitPercent, persist on `sales`, and survive amend/recompute (the Readjustment
            // pair never did). The backend Readjustment branch stays in place so the 7
            // historical sales that used it still recompute correctly.
            // The agent's Site Visit answers travel with the sale, not the customer —
            // they describe this job at this address.
            "referralPaidBy": referralPaidByAstern ? "Astern" : "Agent",
            "siteGatedGuarded": siteGatedGuarded,
            "mgmtApprovalNeeded": mgmtApprovalNeeded,
            "siteVisitRequired": siteVisitRequired,
            "createCustomerRequest": {
                "customerName": customerName.toUpperCase(),
                "email": (customerEmail || '').toLowerCase(),
                "phoneNumber": customerPhone,
                "address": (customerAddress || '').toUpperCase(),
                "state": customerState || null,
                "city": customerCity || null,
                "postcode": customerPostcode || null
            }
        };

        setSubmitting(true);

        try {
            const response = await createSales(newSalesInfo);
            if (response.status === 201) {
                setSuccessMsg("Sales created successfully!");
                // Open the new sale's page so they can add docs / signature next.
                const newId = response.data?.salesId;
                /* Came from a referral lead: record which sale it became, so Referral Leads
                   stops saying "with the team". Deliberately AFTER the sale exists and
                   deliberately not fatal — the sale is made either way, and a lead left
                   unstamped is a cosmetic gap, not lost money. The fee itself rides on
                   referralUserId in the payload above, not on this call. */
                const leadId = location.state?.referralLeadId;
                if (newId && leadId) {
                    try { await attachSaleToLead({ referralEnquiryId: leadId, salesId: newId }); }
                    catch (e) { console.error('Could not link the referral lead to the sale:', e); }
                }
                setTimeout(() => {
                    navigate(newId ? `/accounting/sales/${newId}` : "/sales");
                }, 1000);

                return;
            }
            else {
                alert("Sales creation failed. Please check again on the details.");
                console.error("Unexpected response:", response);
            }
        }
        catch (error) {
            if (error.response?.status === 401) {
                alert("Session expired. Please log in again.");
                navigate('/login');

                return;
            }
            // Surface backend validation messages (e.g. referrer rate exceeds agent PSC%).
            // Backend returns BadRequest with { message } payload — fall back to a string body
            // when present, then to the generic notice if neither is available.
            const data = error.response?.data;
            const serverMsg = (data && (data.message || data.title))
                || (typeof data === 'string' ? data : '');
            alert(serverMsg || "Sales creation errored. Please check again on the details.");
            console.error("Error creating sales:", error.response?.data || error);
        }
        setSubmitting(false);
    }

    // if (agentLoading) {
    //     return <div>Loading...</div>;
    // }

    // if (error) {
    //     return <div className="alert alert-danger">{error}</div>;
    // }

    return (
        <Box sx={{ width: '100%' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
                sx={{ mb: 2, fontFamily: PP, textTransform: 'none', color: '#64748B', fontWeight: 500, fontSize: '0.85rem' }}>
                Back
            </Button>

            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', mb: 0.5 }}>
                Create New Sale
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#64748B', fontFamily: PP, mb: 2 }}>
                Fill in the details below to register a new sale
            </Typography>

            {successMsg && (
                <Box sx={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px', p: 1.5, color: '#065F46', fontSize: '0.85rem', mb: 2, fontFamily: PP }}>
                    {successMsg}
                </Box>
            )}

            <form onSubmit={handleSubmit}>
            {/* Two-column layout mirrors the sale page: left = form + form details,
                right = sticky Create action card. */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.5fr) minmax(380px, 1fr)' },
                gap: 2,
                alignItems: 'start',
            }}>
                {/* Left column */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                    {/* "Discount Funded By" is RETIRED on create-sales (spec §12a).
                        GenericSalesForm renders that control only when `setDiscountFundedBy`
                        is supplied, so simply NOT passing it (below) removes the control here
                        while leaving the shared component — and the edit page — untouched. */}
                    <GenericSalesForm
                        mode="create"
                        rscSeeded={!!location.state?.rscPrefill}
                        agentGroup={agentGroup}
                        agentNameOptions={agentNameOptions}
                        selectedAgentId={selectedAgentId}
                        setSelectedAgentId={setSelectedAgentId}
                        coBrokeGroupOptions={coBrokeGroupOptions}
                        coBrokeNameOptions={coBrokeNameOptions}
                        selectedCoBrokeGroupId={selectedCoBrokeGroupId}
                        selectedCoBrokeAgentId={selectedCoBrokeAgentId}
                        setSelectedCoBrokeAgentId={setSelectedCoBrokeAgentId}
                        referralOptions={referralOptions}
                        onCreateReferral={() => setQuickReferralOpen(true)}
                        referralPaidByAstern={referralPaidByAstern}
                        setReferralPaidByAstern={setReferralPaidByAstern}
                        canSetReferralPayer={isAdminLike}
                        currentUserId={user?.userId || userId}
                        selectedReferralId={selectedReferralId}
                        setSelectedReferralId={setSelectedReferralId}
                        customerName={customerName}
                        setCustomerName={setCustomerName}
                        customerEmail={customerEmail}
                        setCustomerEmail={setCustomerEmail}
                        customerPhone={customerPhone}
                        setCustomerPhone={setCustomerPhone}
                        customerAddress={customerAddress}
                        setCustomerAddress={setCustomerAddress}
                        siteGatedGuarded={siteGatedGuarded}
                        setSiteGatedGuarded={setSiteGatedGuarded}
                        mgmtApprovalNeeded={mgmtApprovalNeeded}
                        setMgmtApprovalNeeded={setMgmtApprovalNeeded}
                        siteVisitRequired={siteVisitRequired}
                        setSiteVisitRequired={setSiteVisitRequired}
                        customerState={customerState}
                        setCustomerState={setCustomerState}
                        customerCity={customerCity}
                        setCustomerCity={setCustomerCity}
                        customerPostcode={customerPostcode}
                        setCustomerPostcode={setCustomerPostcode}
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                        cableBrand={cableBrand}
                        setCableBrand={setCableBrand}
                        phaseMode={phaseMode}
                        setPhaseMode={setPhaseMode}
                        pricingSnapshotId={pricingSnapshotId}
                        setPricingSnapshotId={setPricingSnapshotId}
                        pricingSnapshots={pricingSnapshots}
                        atapPanelWatt={atapPanelWatt}
                        panelQty={panelQty}
                        setPanelQty={setPanelQty}
                        energy={energy}
                        setEnergy={setEnergy}
                        initialPanelWattage={rscPanelWatt}
                        originalPrice={originalPrice}
                        setOriginalPrice={setOriginalPrice}
                        availablePaymentMethods={availablePaymentMethods}
                        paymentMethod={paymentMethod}
                        setPaymentMethod={setPaymentMethod}
                        ccBank={ccBank} setCcBank={setCcBank}
                        ccBankMethod={ccBankMethod} setCcBankMethod={setCcBankMethod}
                        mixCashAmount={mixCashAmount} setMixCashAmount={setMixCashAmount}
                        mixTerm={mixTerm} setMixTerm={setMixTerm}
                        canEditPrice={canEditPrice}
                        residentialMatch={residentialMatch}
                        onSizingChange={setSizing}
                        finalPrice={finalPrice}
                        addOnPrice={addOnPrice}
                        setAddOnPrice={setAddOnPrice}
                        depositOn={depositOn}
                        setDepositOn={setDepositOn}
                        asternDiscount={asternDiscount}
                        setAsternDiscount={handleAsternChange}
                        agentDiscount={agentDiscount}
                        uplineDiscount={Number(uplineDiscount) || 0}
                        teamDiscount={Number(teamDiscount) || 0}
                        setUplineDiscount={setUplineDiscount}
                        setTeamDiscount={setTeamDiscount}
                        setAgentDiscount={setAgentDiscount}
                        deductSst={deductSst}
                        setDeductSst={setDeductSst}
                        handleAgentGroupChange={handleAgentGroupChange}
                        lineItems={lineItems}
                        setLineItems={setLineItems}
                        canEditAddons={true}
                        showPromos={true}
                        promoBackupbox={promoBackupbox} setPromoBackupbox={setPromoBackupbox}
                        promoFirst640={promoFirst640} setPromoFirst640={setPromoFirst640}
                        promoPerBatt={promoPerBatt} setPromoPerBatt={setPromoPerBatt}
                    />
                </Paper>

                {/* Form Details (for PDFs) — same collapsible section as the sale page */}
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                    <Box
                        onClick={() => setFormDetailsOpen(o => !o)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    >
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.95rem' }}>
                                Form Details (for PDFs)
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', fontFamily: PP }}>
                                Customer / document info used only by the generated forms — not part of the sale.
                            </Typography>
                        </Box>
                        <IconButton size="small" sx={{ color: '#64748B' }}>
                            {formDetailsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Collapse in={formDetailsOpen} unmountOnExit>
                        <Box sx={{ mt: 2 }}>
                            <SaleFormDetails
                                payload={formPayload}
                                onChange={handleFormFieldChange}
                                canEdit={true}
                                lockedFields={{
                                    name: (customerName || '').toUpperCase(),
                                    phone: customerPhone || '',
                                    email: (customerEmail || '').toLowerCase(),
                                    address: combinedAddress,
                                }}
                            />
                        </Box>
                    </Collapse>
                </Paper>
                </Box>

                {/* Right column: sticky Create action card (mirrors the sale page's Save box) */}
                <Box sx={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                        <Typography sx={{ fontWeight: 700, fontFamily: PP, color: '#0F172A', fontSize: '0.95rem', mb: 1.5 }}>
                            New Sale
                        </Typography>
                        {dupMatches.length > 0 && (
                            <Alert severity="warning" sx={{ mb: 1.5, fontFamily: PP, '& .MuiAlert-message': { fontFamily: PP, fontSize: '0.78rem' } }}>
                                <Typography sx={{ fontWeight: 700, fontFamily: PP, fontSize: '0.8rem', mb: 0.5 }}>
                                    Possible duplicate — {dupMatches.length} existing sale{dupMatches.length === 1 ? '' : 's'} match this customer:
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                    {dupMatches.map(m => (
                                        <li key={m.salesId}>
                                            #{m.salesId} — {m.customerName || '—'}
                                            {m.agentName ? ` · ${m.agentName}` : ''}
                                            {m.status ? ` · ${m.status}` : ''}
                                            {m.depositOn ? ` · ${new Date(m.depositOn).toLocaleDateString()}` : ''}
                                        </li>
                                    ))}
                                </Box>
                            </Alert>
                        )}
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={submitting}
                            sx={{
                                fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.9rem',
                                borderRadius: '10px', py: 1.1, boxShadow: 'none',
                                background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
                            }}
                        >
                            {submitting ? 'Creating…' : 'Create Sale'}
                        </Button>
                        <Button
                            type="button"
                            fullWidth
                            variant="outlined"
                            onClick={handleCancel}
                            sx={{
                                mt: 1, fontFamily: PP, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                                borderRadius: '10px', borderColor: '#CBD5E1', color: '#64748B',
                            }}
                        >
                            Cancel
                        </Button>
                        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: PP, mt: 1.5 }}>
                            After creating, you'll be taken to the sale page to upload documents and request the signature.
                        </Typography>
                    </Paper>
                </Box>
            </Box>
            </form>

            <QuickAddReferralDialog
                open={quickReferralOpen}
                onClose={() => setQuickReferralOpen(false)}
                onCreated={handleReferralCreated}
            />
        </Box>
    );
}
